// @ts-nocheck - TODO: properly type this file!

// React
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// antd
import { Empty, Flex, Form } from 'antd';
// components
import { getAlertDisplayName } from '@/common/alertType';
import {
  CommonDrawer,
  CustomModalWithoutButton,
  CustomParagraph,
  CustomSelect,
  CustomTitle,
  SearchInput,
} from '../../../components';
import AlertCustomizationInput from './AlertCustomizationInput.jsx';
import AlertCopySettingsForm from './partials/AlertCopySettingsForm.jsx';
import AlertCustomizationForm from './partials/AlertCustomizationForm.jsx';
// API's
import {
  AlertGroup,
  GetAlertsAvailableActions,
  GetAlertsCustomization,
} from '../../../apis/settings.js';
import {
  AlertConfigData,
  FeaturePermissionMatrix,
  MenuItemData,
} from '../../../apis/types';
import { getTeamsDDLOFFeature } from '../../../common/utils/dropDown';
// helpers
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext.jsx';
import {
  AlertCategory,
  AlertCustomizationConfig,
} from './AlertCustomizationConfig';
// icons / images
import theme from '@/assets/theme';
import { MenuLabels, SubMenuLabels } from '@/common/enum';
import { themeT } from '@/utils/themeText';
import { CopyIconAlertCustomization } from '../../../assets/icons/icons.jsx';
import SensorInfoIcon from '../../../assets/images/SensorInfoIcon.jsx';
import SensorInfoIconDisabled from '../../../assets/images/SensorInfoIconDisabled.jsx';
import { useMenuPermissions, useUnAuthorize } from '../../../hooks';
import AddActionDrawer from './partials/AddActionDrawer';
import { ActionItem, AlertCustomizationProps } from './type';
import styles from './AlertCustomization.module.scss';

export interface TeamOption {
  id?: string;
  name?: string;
  label: string;
  value: string;
  buttonDisabled?: boolean;
}

export interface Team {
  id?: string;
  name?: string;
  permissions?: FeaturePermissionMatrix;
}

export interface ThresholdValues {
  [key: string]: unknown;
}

export interface RootState {
  menu: {
    items: MenuItemData[];
  };
  teams: {
    list: Team[];
  };
}

const setupSensorRuleIds = AlertCustomizationConfig.flatMap(
  (group) => group.rules
)
  .filter((rule) => rule.showSensorInfoIconDisabled)
  .map((rule) => rule.id);

/**
 * Alert Customization page component for managing alert thresholds and settings per team
 *
 * @param title - Page title
 */
const AlertCustomization: React.FC<AlertCustomizationProps> = ({ title }) => {
  const { token, decodedUserToken } = useToken();
  const { unauthorize } = useUnAuthorize();
  const isCompanyOwner = decodedUserToken()?.is_company_owner;
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const AlertCustomizationPermissions = useMenuPermissions(
    MenuLabels.Settings,
    SubMenuLabels.AlertCustomization
  );
  const [teamsOptions, setTeamsOptions] = useState<TeamOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>(null);
  const [selectedAlertData, setSelectedAlertData] = useState<AlertConfigData>(
    {} as AlertConfigData
  );
  const teams = useSelector((state: RootState) => state.teams.list);
  const [_buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
  const [_allTeamThresholds, setAllTeamThresholds] = useState<ThresholdValues>(
    {}
  );
  const [_thresholds, setThresholds] = useState<ThresholdValues>({});
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [patchedConfigJSON, setPatchedConfigJSON] = useState<AlertGroup[]>([]);
  const [filteredItems, setFilteredItems] =
    useState<AlertGroup[]>(patchedConfigJSON);
  const [form] = Form.useForm();
  const [tpSelected, setTpSelected] = useState<ActionItem[]>([]);
  const [selectedAction, setSelectedAction] = useState<string>('');
  const action = 'gather_information';
  const handleSelectedAlertData = (data: AlertConfigData) => {
    if (data?.alert_type === 'transport_provider_exception') return;
    setSelectedAlertData(data);
    setShowDrawer(true);
    setTpSelected(
      (data?.preferred_actions || []).filter((item) => item.id != null)
    );
    setSelectedAction(data?.recommended_action_type || action);
    setDrawerState('detail');
  };
  const patchThresholdValues = useCallback(
    // TODO: properly type this!
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config: AlertCategory[], apiData: any): unknown[] => {
      const updated = JSON.parse(JSON.stringify(config));
      const setupSensor = apiData?.setup_sensor;
      updated.forEach((group) => {
        group.rules.forEach((rule) => {
          const key = rule.id;
          if (Object.prototype.hasOwnProperty.call(apiData, key)) {
            rule.defaultValue = apiData[key];
          }
          if (setupSensorRuleIds.includes(rule.id)) {
            rule.showSetupSensorButton = !!setupSensor;
            rule.infoIconImage = setupSensor
              ? SensorInfoIconDisabled
              : SensorInfoIcon;
          }
        });
      });

      return updated;
    },
    []
  );

  const fetchThresholds = useCallback(async () => {
    try {
      showSpin();
      let query = '';
      if (selectedTeam === 'all') {
        query = '';
      } else if (Array.isArray(selectedTeam) && selectedTeam.length > 0) {
        query = `?team=${selectedTeam[0]}`;
      } else if (typeof selectedTeam === 'string') {
        query = `?team=${selectedTeam}`;
      }
      const response = await GetAlertsCustomization(token, query);
      const fetchedData = response?.data?.data?.data;

      if (fetchedData) {
        const patched = patchThresholdValues(
          AlertCustomizationConfig,
          fetchedData
        );
        // @ts-expect-error TODO: properly type this!
        setPatchedConfigJSON(fetchedData || []);
        const newThresholds = {};
        patched.forEach((group) => {
          // @ts-expect-error TODO: properly type this!
          group.rules.forEach((rule) => {
            newThresholds[rule.id] = rule.defaultValue;
          });
        });

        setThresholds(newThresholds);
        if (!query) {
          setAllTeamThresholds(newThresholds);
        }
      }
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  }, [
    hideSpin,
    patchThresholdValues,
    selectedTeam,
    showSpin,
    token,
    unauthorize,
  ]);

  const [actionsList, setActionsList] = useState<ActionItem[]>([]);
  const fetchAvailableActions = useCallback(async () => {
    GetAlertsAvailableActions(token)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            if (data) {
              setActionsList(data?.data?.data || []);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  }, [token, unauthorize]);

  useEffect(() => {
    fetchAvailableActions().catch(console.error);
  }, [fetchAvailableActions]);

  useEffect(() => {
    getTeamsDDLOFFeature(token, 'Alert Customization')
      .then((response) => {
        if (response?.length) {
          const transformed = response.map((team) => ({
            label: team.name,
            value: team.id,
            buttonDisabled: !(
              team.permissions?.is_create || team.permissions?.is_update
            ),
          }));

          const finalOptions = isCompanyOwner
            ? [{ label: 'Default template', value: 'all' }, ...transformed]
            : [...transformed];
          setTeamsOptions(finalOptions);

          const firstValue = finalOptions[0]?.value;
          setSelectedTeam(firstValue);
          form.setFieldsValue({ teams: firstValue });
        }
      })
      .catch(console.error);
  }, [token, isCompanyOwner, form]);

  useEffect(() => {
    fetchThresholds().catch(console.error);
  }, [fetchThresholds, selectedTeam]);

  // drawer
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [drawerState, setDrawerState] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const handleCloseDrawer = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      setShowDrawer(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };
  const onSuccessCopyModal = () => {
    setModalOpen(false);
    fetchThresholds().catch(console.error);
  };

  const filterOptions = (value: string) => {
    if (!value) {
      setFilteredItems(patchedConfigJSON);
      return;
    }
    const lower = value.toLowerCase();
    const filtered = patchedConfigJSON
      .map((phaseItem) => {
        const matchedAlerts = phaseItem?.alerts.filter((alert) => {
          const nameMatch =
            getAlertDisplayName(alert?.alert_type, alert?.alert_name)
              ?.toLowerCase()
              .includes(lower) ||
            alert?.dynamic_banner_text?.toLowerCase().includes(lower);
          const contactsMatch =
            alert?.recommended_contacts?.some((c) =>
              c?.name.toLowerCase().includes(lower)
            ) || false;
          return nameMatch || contactsMatch;
        });
        return {
          ...phaseItem,
          alerts: matchedAlerts,
        };
      })
      .filter((phaseItem) => phaseItem?.alerts?.length > 0);
    setFilteredItems(filtered);
  };
  useEffect(() => {
    setFilteredItems(patchedConfigJSON);
  }, [patchedConfigJSON]);

  const toggleDrawer = (val: string) => {
    setDrawerState?.(val);
  };
  return (
    <>
      <div className={styles.scroller}>
        <Flex vertical gap={8}>
          <CustomTitle
            text={title}
            fontSize={20}
            fontWeight='700'
            color='var(--text-primary)'
            marginBottom='0px'
          />
          <Flex justify='space-between' align='center' gap={8}>
            <CustomParagraph
              text={`Customize thresholds and messages for each alert type. These will be applied to all ${themeT('team', theme.themeType).toLowerCase()} shipments.`}
              fontSize='14px'
              fontWeight='400'
              lineHeight='20px'
              marginBottom='0px'
            />

            <SearchInput
              placeholder='Search...'
              onChange={filterOptions}
              onClear={() => setFilteredItems(patchedConfigJSON)}
              borderWidth='2px'
            />
          </Flex>
        </Flex>
        {!(
          teams.length === 1 && teams[0]?.name?.toLowerCase() === 'default'
        ) && (
          <>
            <div role='separator' className={styles.divider} />

            <Flex align='center' gap={8} style={{ marginBottom: 5 }}>
              <div className='alert-teams-row teams-title'>
                <CustomTitle
                  text={`${themeT('team', theme.themeType)}`}
                  fontSize={14}
                  fontWeight='600'
                  style={{ minWidth: '50px' }}
                />
              </div>
              <Form
                form={form}
                initialValues={{
                  teams: selectedTeam || '',
                }}
              >
                <Form.Item
                  name='teams'
                  className='alert-teams-row'
                  rules={[
                    {
                      required: true,
                      message: `Please select at least one ${themeT('team', theme.themeType)}`,
                    },
                  ]}
                >
                  <CustomSelect
                    placeholder='Select team'
                    optionClassName={
                      isCompanyOwner
                        ? 'custom-form-item-team-alert-thresholds'
                        : ''
                    }
                    options={teamsOptions}
                    onChange={(val) => {
                      setSelectedTeam(val);
                      form.setFieldsValue({ teams: val });
                      const selected = teamsOptions.find(
                        (t) => t.value === val
                      );
                      setButtonsDisabled(!!selected?.buttonDisabled);
                    }}
                  />
                </Form.Item>
              </Form>
              {AlertCustomizationPermissions?.is_create ||
              AlertCustomizationPermissions?.is_update ? (
                <button
                  onClick={() => setModalOpen(true)}
                  className='hoverable-icon alert-copy-setting'
                >
                  <CopyIconAlertCustomization />
                </button>
              ) : null}
            </Flex>
          </>
        )}

        <div role='separator' className={styles.divider} />
        <>
          {filteredItems?.map((group: AlertGroup, index) => (
            <React.Fragment key={group.category}>
              <Flex vertical align='flex-start'>
                <Flex vertical style={{ width: '100%' }} gap={16}>
                  <AlertCustomizationInput
                    group={group}
                    teamsOptions={teamsOptions}
                    selectedTeam={selectedTeam}
                    navigate={navigate}
                    handleSelectedAlertData={handleSelectedAlertData}
                    refreshAlerts={fetchThresholds}
                    AlertCustomizationPermissions={
                      AlertCustomizationPermissions
                    }
                  />
                </Flex>
              </Flex>

              {index < AlertCustomizationConfig?.length - 1 && (
                <div role='separator' className={styles.dividerBetweenGroups} />
              )}
            </React.Fragment>
          ))}
        </>
      </div>
      {filteredItems?.length === 0 && (
        <div className={`flex-center x y ${styles.empty}`}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
      <CommonDrawer
        open={showDrawer}
        onClose={() => handleCloseDrawer()}
        width={552}
        onBack={
          drawerState === 'actions' ? () => toggleDrawer('detail') : undefined
        }
      >
        <div className={`${drawerState === 'detail' ? 'd-block' : 'd-none'}`}>
          <AlertCustomizationForm
            onClose={() => handleCloseDrawer()}
            selectedAlertData={selectedAlertData}
            selectedTeam={selectedTeam}
            refreshAlerts={fetchThresholds}
            patchedConfigJSON={patchedConfigJSON}
            AlertCustomizationPermissions={AlertCustomizationPermissions}
            setDrawerState={setDrawerState}
            actionsList={actionsList}
            tpSelected={tpSelected}
            setTpSelected={setTpSelected}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
            setShowDrawer={setShowDrawer}
            showDiscardModal={showDiscardModal}
            setShowDiscardModal={setShowDiscardModal}
            setSelectedAction={setSelectedAction}
            selectedAction={selectedAction}
          />
        </div>

        <div className={`${drawerState === 'actions' ? 'd-block' : 'd-none'}`}>
          <AddActionDrawer
            actionsList={actionsList}
            tpSelected={tpSelected}
            setTpSelected={setTpSelected}
            drawerState={drawerState}
            setDrawerState={setDrawerState}
          />
        </div>
      </CommonDrawer>

      {modalOpen && (
        <CustomModalWithoutButton
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          modalTitle={`Duplicate alert settings`}
          fontSize={'20px'}
          lineHeight='28px'
          modalText={`Copy alert settings from one ${themeT('team', theme.themeType)?.toLowerCase()} to another`}
          modalTextFontSize={'14px'}
          modalSize={600}
          modalBorderRadius={'16px'}
        >
          <AlertCopySettingsForm
            closeModal={closeModal}
            teamsOptions={teamsOptions}
            onSuccess={onSuccessCopyModal}
          />
        </CustomModalWithoutButton>
      )}
    </>
  );
};

export default AlertCustomization;
